import * as marked from 'marked';

declare const clipboardData: any;

export function copyToClipboard(text: string) {
    if ((window as any).clipboardData && (window as any).clipboardData.setData) {
        // IE specific code path to prevent textarea being shown while dialog is visible.
        return clipboardData.setData("Text", text);
    }
    else if (document.queryCommandSupported && document.queryCommandSupported("copy")) {
        const textarea = document.createElement("textarea");
        textarea.textContent = text;
        textarea.style.position = "fixed";  // Prevent scrolling to bottom of page in MS Edge.
        document.body.appendChild(textarea);
        textarea.select();

        try {
            return document.execCommand("copy");  // Security exception may be thrown by some browsers.
        }
        catch (ex) 
        {
            console.warn("Copy to clipboard failed.", ex);
            return false;
        }
        finally
        {
            document.body.removeChild(textarea);
        }
    }
}


declare global {
    interface Number
    {
        toFixedIfNeeded: (x: number) => string;
    }
}

if (typeof Number.prototype.toFixedIfNeeded != 'function') {
    Number.prototype.toFixedIfNeeded = function (x: number) {
        const str = this.toString();
        const isDecimal = str.indexOf(".") != -1 || str.indexOf(",") != -1;

        if (isDecimal) {
            return this.toFixed(x);
        }

        return str;
    };
}

if (typeof String.prototype.startsWith != 'function') {
    String.prototype.startsWith = function (str) {
        return this.indexOf(str) == 0;
    };
}


declare global {
    interface Array<T> {
        remove: (item: T) => void;
    }
}

if (typeof Array.prototype.remove != 'function') {
    Array.prototype.remove = function <T>(this: T[], item: T): void {
        const index = this.indexOf(item);

        if (index > -1) {
            this.splice(index, 1);
        }
    };
}

export function arrayMove<T>(arr: T[], fromIndex: number, toIndex: number): void
{
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= arr.length || toIndex >= arr.length)
    {
        return;
    }

    if (fromIndex < toIndex)
    {
        toIndex--;
    }

    const element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
}

export function convertToDate(value: unknown): Date | null {
    if (value == null) {
        return null;
    }

    if (typeof value === 'string' || typeof value === 'number') {
        const date = new Date(value);

        if (!isNaN(date.getTime())) {
            return date;
        }
    }
    else if (value instanceof Date && !isNaN(value.getTime())) {
        return value;
    }

    return null;
}

export function localToUtcDate(localDate: Date | null): Date | null
{
    return localDate ? new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 0, 0, 0, 0)) : null;
}

export function parseOptionalNumber(input: string | null | undefined): number | null
{
    const trimmed = input?.trim();
    if (!trimmed) return null;

    const num = Number(trimmed);
    return isNaN(num) ? null : num;
}


export function numCompare(num1: number, num2: number): number {
    if (num1 < num2) return -1;
    if (num1 > num2) return 1;
    return 0;
}

export function boolCompare(bool1: boolean, bool2: boolean): number {
    return (bool1 === bool2) ? 0 : bool1 ? -1 : 1;
}

export function strCompare(str1: string, str2: string): number {
    if (str1 < str2) return -1;
    if (str1 > str2) return 1;
    return 0;
}

export function strCompareLowerCase(str1: string, str2: string): number {
    return strCompare(('' + str1).toLowerCase(), ('' + str2).toLowerCase());
}

export function dateCompare(dt1: Date, dt2: Date): number {
    if (dt1 < dt2) return -1;
    if (dt1 > dt2) return 1;
    return 0;
}

export function dateCompare1(dt1: string | null, dt2: string | null): number {
    if (dt1 === null && dt2 === null) return 0;
    if (dt1 === null) return -1;
    if (dt2 === null) return 1;

    const date1 = new Date(dt1);
    const date2 = new Date(dt2);

    if (date1 < date2) return -1;
    if (date1 > date2) return 1;
    return 0;
}


export function average(values: number[]): number | null {
    if (values == null || values.length == 0) {
        return null;
    }

    const sum = values.reduce((sum, current) => sum + current, 0);
    const avg = sum / values.length;

    return avg;
}

export function sumOrDefault(values: (number | null)[]): number | null
{
    const filtered = values?.filter(v => v != null) ?? [];

    if (!filtered.length) return null;

    return filtered.reduce((s, c) => s + c, 0);
}


export function isNullOrWhitespace(input: string | null | undefined): boolean {
    return !input || !input.trim();
}


type MarkedLinkFunction = (link: marked.Tokens.Link) => string;
const tchMarkedLinkFn: MarkedLinkFunction = ({ href, title, text }) => `<a target="_blank" href="${href}" title="${title}">${text}</a>`;

export function tchMarked(input: string): string | null {
    const renderer = new marked.Renderer();
    renderer.link = tchMarkedLinkFn;
    return isNullOrWhitespace(input) ? null : marked.parse(input, { async: false, renderer: renderer, breaks: true }) as string;
}

export function GetVhPixels(vPercent: number): number {
    const h = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    return (vPercent * h) / 100;
}

export function roundDecimal(value: number, decimals: number): number {
    const x = Math.pow(10, decimals);
    value = Math.round(value * x) / x;
    return value;
}

export function ellipsis(text: string, maxLength: number): string {
    return text.length > maxLength ? `${text.substring(0, maxLength - 3)}...` : text;
}

export function splitString(str: string, maxLength: number) {
    const strs = [];
    while (str.length > maxLength) {
        let pos = str.substring(0, maxLength).lastIndexOf(' ');
        pos = pos <= 0 ? maxLength : pos;
        strs.push(str.substring(0, pos));
        let i = str.indexOf(' ', pos) + 1;
        if (i < pos || i > pos + maxLength)
            i = pos;
        str = str.substring(i);
    }
    strs.push(str);
    return strs;
}


export function getTimestamp(): string {
    const date = new Date();
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
}

export function insertIntoTextArea1(textArea: HTMLTextAreaElement, txtToAdd: string) {
    //https://stackoverflow.com/questions/1064089/inserting-a-text-where-cursor-is-using-javascript-jquery

    const scrollPos = textArea.scrollTop;
    let caretPos = textArea.selectionStart;

    const front = (textArea.value).substring(0, caretPos);
    const back = (textArea.value).substring(textArea.selectionEnd, textArea.value.length);

    textArea.value = front + txtToAdd + back;
    caretPos = caretPos + txtToAdd.length;
    textArea.selectionStart = caretPos;
    textArea.selectionEnd = caretPos;
    textArea.focus();
    textArea.scrollTop = scrollPos;
}

export function toEntityValuesString(entityType: string, entityId: string, values: number[]): string {
    return `${entityType}:${entityId}:${values.join(';')}`
}

export function toKeyIntPair(key: string, value: number | null): string {
    return `${key}:${value}`
}

export function toEntriesState(entries: string[]): string {
    return entries.join(";");
}

export function generateName(type: string): string
{
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is zero-indexed
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${type} (${day}.${month}.${year} ${hours}:${minutes}:${seconds})`;
}

export function formatStringForDateInput(dateStr: string | null): string | null
{
    if (dateStr == null) return null;

    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}



if (!Array.prototype.find)
{
    Object.defineProperty(Array.prototype, 'find', {
        value: function <T>(predicate: (value: T, index: number, obj: T[]) => boolean, thisArg?: any): T | undefined
        {
            // 1. Let O be ? ToObject(this value).
            if (this == null)
            {
                throw new TypeError('"this" is null or not defined');
            }

            const o = Object(this) as T[];

            // 2. Let len be ? ToLength(? Get(O, "length")).
            const len = o.length >>> 0;

            // 3. If IsCallable(predicate) is false, throw a TypeError exception.
            if (typeof predicate !== 'function')
            {
                throw new TypeError('predicate must be a function');
            }

            // 5. Let k be 0.
            let k = 0;

            // 6. Repeat, while k < len
            while (k < len)
            {
                const kValue = o[k];

                if (predicate.call(thisArg, kValue, k, o))
                {
                    return kValue;
                }

                k++;
            }

            // 7. Return undefined.
            return undefined;
        },
        configurable: true,
        writable: true
    });
}


export function getDistinctBgCss(orderValue: number): string {
    //const classes: string[] = [
    //    'bg-DarkBlue',
    //    'bg-DarkGreen',
    //    'bg-DarkRed',
    //    'bg-DarkOrange',
    //    'bg-DarkPurple',
    //    'bg-MidBlue',
    //    'bg-MidGreen',
    //    'bg-MidRed',
    //    'bg-MidOrange',
    //    'bg-MidPurple',
    //    'bg-LightBlue',
    //    'bg-LightGreen',
    //    'bg-LightRed',
    //    'bg-LightOrange',
    //    'bg-MidPink',
    //    'bg-BrightBlue',
    //    'bg-BrightGreen',
    //    'bg-BrightRed',
    //    'bg-BrightOrange',
    //    'bg-BrightPink'
    //];

    const classes: string[] = [
        'bg-tch-gray',
        'bg-tch-orange',
        'bg-tch-red',
        'bg-tch-grassGreen',
        'bg-tch-blue',
        'bg-tch-fuchsia',
        'bg-tch-green',
        'bg-tch-electricBlue',
        'bg-tch-silver',
        'bg-tch-citrine',
        'bg-tch-blueGray',
        'bg-tch-tan',
        'bg-tch-black',
        'bg-tch-purple',
        'bg-tch-thistle',
        'bg-tch-gold',
        'bg-tch-darkYellow',
        'bg-tch-coffee',
        'bg-tch-raspberry',
        'bg-tch-chartreuse'
    ];

    if (orderValue >= classes.length) {
        return getDistinctBgCss(orderValue - classes.length);
    }

    return classes[orderValue];
}

export function getDistinctColor(orderValue: number) {
    //const colors: string[] = [
    //    '#000066',
    //    '#003300',
    //    '#660000',
    //    '#CC6600',
    //    '#660066',
    //    '#0033CC',
    //    '#006600',
    //    '#990000',
    //    '#FF6600',
    //    '#990066',
    //    '#0099FF',
    //    '#00CC00',
    //    '#CC0000',
    //    '#FF9900',
    //    '#CC0099',
    //    '#00CCFF',
    //    '#00FF00',
    //    '#FF0000',
    //    '#FFCC00',
    //    '#FF00CC'
    //];

    const colors: string[] = [
        '#56565a',
        '#ff9924',
        '#ff0e00',
        '#7cfc00',
        '#004A87',
        '#FF00FF',
        '#80BD01',
        '#7DF9FF',
        '#C0C0C0',
        '#E4D00A',
        '#7393B3',
        '#D2B48C',
        '#000',
        '#800080',
        '#D8BFD8',
        '#FFD700',
        '#8B8000',
        '#6F4E37',
        '#E30B5C',
        '#DFFF00'
    ];

    if (orderValue >= colors.length) {
        return getDistinctColor(orderValue - colors.length);
    }

    return colors[orderValue];
}


export function isUsDateFormat(): boolean {
    const languages = navigator.languages === undefined ? [navigator.language] : navigator.languages;

    if (languages == null || languages.length === 0) {
        return false;
    }

    return languages[0].trim().endsWith("-US");
}


export enum EditorState {
    Closed = 'Closed',
    Creator = 'Creator',
    Editor = 'Editor',
    Cloner = 'Cloner',
    Preview = 'Preview',
}

export enum SavingState {
    InProgress = 'InProgress',
    Pending = 'Pending',
    Saved = 'Saved'
}

export function normalizeToZeroIfClose(value: number, tolerance: number = 1e-6): number
{
    // Check if the value is effectively zero
    return Math.abs(value) < tolerance ? 0 : value;
}
export function toStringSmartly(
    number: number | null,
    threshold: number = 1000,
    decimalPlaces: number = 2,
    locale: string = "en-US"
): string | null
{
    // Handle the case when the number is null
    if (number === null)
    {
        return null;
    }

    number = normalizeToZeroIfClose(number);

    // If the number exceeds the threshold, format it without decimal places
    if (Math.abs(number) >= threshold)
    {
        return new Intl.NumberFormat(locale, {
            maximumFractionDigits: 0, // No decimal places for large numbers
        }).format(number);
    }
    else
    {
        // Create the formatter only if necessary
        const formatter = new Intl.NumberFormat(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: decimalPlaces,
        });

        // Get the decimal separator using formatToParts
        const decimalSeparator = formatter.formatToParts(1.1).find(part => part.type === 'decimal')?.value || '.';

        // Format the number with the specified decimal places
        let formattedNumber = formatter.format(number);

        // Remove trailing zeros after the decimal point
        const regex = new RegExp(`\\${decimalSeparator}0+$`); // Remove trailing zeros after decimal separator
        formattedNumber = formattedNumber.replace(regex, "").replace(new RegExp(`\\${decimalSeparator}$`), "");

        return formattedNumber;
    }
}




export function toPercentageString(
    value: number | null,
    decimalPlaces: number = 1,
    locale: string = "en-US"
): string | null
{
    // Handle the case when the value is null
    if (value === null)
    {
        return null;
    }

    value = normalizeToZeroIfClose(value);

    // Use Intl.NumberFormat to format as a percentage
    return new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(value);
}

export function calculateProportionOf(value: number | null, total: number | null): number | null
{
    return total !== null && total !== 0 ? (value !== null ? value / total : null) : null;
}


export function toIsoDateStringUtc(date: Date | null): string | null
{
    if (!date) return null;

    // Extract UTC year, month, and day
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Month is zero-based
    const day = String(date.getUTCDate()).padStart(2, '0');

    // Return the formatted ISO 8601 date string
    return `${year}-${month}-${day}`;
}





